import { serializeBaselineScope } from "../baselines/records";
import type { BaselineRecord } from "../baselines/types";
import { finiteNonNegativeEncounterVolume } from "./encounter-volumes";
import {
  mergeOrgProfileFacts,
  type OrgProfileFact,
  orgProfileFactSourceTrust,
  serializeOrgProfileFactScope,
} from "./facts";
import { buildOrgProfileFactsFromProfile } from "./org-profile-facts";
import type { ProfileData } from "./types";

/**
 * The stored-org-profile fields needed to derive the EFFECTIVE profile (facts
 * folded into `profileData` + `baselineRecords`). All optional so both the raw
 * `getOrgProfile` row (server) and the frontend org-profile query can satisfy
 * it structurally; missing fields degrade to facts synthesized from whatever is
 * present.
 */
export type EffectiveOrgProfileInput = {
  name?: unknown;
  orgType?: unknown;
  annualRevenue?: unknown;
  bedCount?: unknown;
  physicianCount?: unknown;
  region?: unknown;
  state?: unknown;
  settingType?: unknown;
  teachingStatus?: unknown;
  ehrSystem?: unknown;
  annualAiBudget?: unknown;
  profileData?: unknown;
  baselineRecords?: unknown;
  tier1Inputs?: unknown;
  facts?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function getEncounterOverrides(tier1Inputs: unknown): Record<string, number> {
  if (!isRecord(tier1Inputs)) return {};
  const encounterOverrides = tier1Inputs.encounterOverrides;
  if (!isRecord(encounterOverrides)) return {};

  return Object.fromEntries(
    Object.entries(encounterOverrides)
      .map(
        ([key, value]) =>
          [key, finiteNonNegativeEncounterVolume(value)] as const,
      )
      .filter((entry): entry is [string, number] => entry[1] != null),
  );
}

function normalizeProfileData(profile: EffectiveOrgProfileInput): ProfileData {
  const profileData = isRecord(profile.profileData)
    ? (profile.profileData as Partial<ProfileData>)
    : {};
  const encounterOverrides = getEncounterOverrides(profile.tier1Inputs);
  if (Object.keys(encounterOverrides).length === 0) {
    return profileData as ProfileData;
  }

  return {
    ...profileData,
    encounter_volumes: {
      ...(profileData.encounter_volumes ?? {}),
      ...encounterOverrides,
    },
  } as ProfileData;
}

function normalizeFact(value: unknown): OrgProfileFact | null {
  if (!isRecord(value) || typeof value.fieldPath !== "string") return null;
  const sourceKind =
    typeof value.sourceKind === "string" ? value.sourceKind : "unknown";
  return {
    fieldPath: value.fieldPath,
    value: value.value,
    sourceKind: sourceKind as OrgProfileFact["sourceKind"],
    sourceLabel:
      typeof value.sourceLabel === "string" ? value.sourceLabel : undefined,
    unit: typeof value.unit === "string" ? value.unit : undefined,
    confidence:
      typeof value.confidence === "number" ? value.confidence : undefined,
    scope: isRecord(value.scope)
      ? (value.scope as OrgProfileFact["scope"])
      : undefined,
    scopeKey:
      typeof value.scopeKey === "string"
        ? value.scopeKey
        : serializeOrgProfileFactScope(
            isRecord(value.scope)
              ? (value.scope as OrgProfileFact["scope"])
              : undefined,
          ),
    derivedFrom: Array.isArray(value.derivedFrom)
      ? (value.derivedFrom.filter(
          (entry): entry is string => typeof entry === "string",
        ) as string[])
      : undefined,
    providedBy:
      typeof value.providedBy === "string" ? value.providedBy : undefined,
    updatedBy:
      typeof value.updatedBy === "string" ? value.updatedBy : undefined,
  };
}

function getFacts(profile: EffectiveOrgProfileInput): OrgProfileFact[] {
  const synthesizedFacts = buildOrgProfileFactsFromProfile(profile);
  if (Array.isArray(profile.facts)) {
    const facts = profile.facts.map(normalizeFact).filter(Boolean);
    if (facts.length > 0) {
      return mergeOrgProfileFacts([
        ...synthesizedFacts,
        ...(facts as OrgProfileFact[]),
      ]);
    }
  }
  return synthesizedFacts;
}

function bestFactsByPath(facts: OrgProfileFact[]): Map<string, OrgProfileFact> {
  const byPath = new Map<string, OrgProfileFact>();
  for (const fact of facts) {
    const prior = byPath.get(fact.fieldPath);
    if (
      !prior ||
      orgProfileFactSourceTrust(fact.sourceKind) >
        orgProfileFactSourceTrust(prior.sourceKind)
    ) {
      byPath.set(fact.fieldPath, fact);
    }
  }
  return byPath;
}

function applyFactValuesToProfileData(
  profileData: ProfileData,
  facts: OrgProfileFact[],
): ProfileData {
  const next: ProfileData = {
    ...profileData,
    encounter_volumes: { ...(profileData.encounter_volumes ?? {}) },
    payer_mix: { ...(profileData.payer_mix ?? {}) },
    specialty_mix: { ...(profileData.specialty_mix ?? {}) },
    workforce: { ...(profileData.workforce ?? {}) },
  } as ProfileData;

  for (const fact of bestFactsByPath(facts).values()) {
    if (typeof fact.value !== "number" || !Number.isFinite(fact.value)) {
      continue;
    }
    const [group, key, child] = fact.fieldPath.split(".");
    if (group === "encounter_volumes" && key) {
      next.encounter_volumes[key] = fact.value;
    } else if (group === "payer_mix" && key) {
      next.payer_mix[key] = fact.value;
    } else if (group === "specialty_mix" && key) {
      next.specialty_mix[key] = fact.value;
    } else if (group === "workforce" && key && child) {
      next.workforce[key] = {
        ...(next.workforce[key] ?? {
          ftes: 0,
          avg_fully_loaded_cost: 0,
        }),
        [child]: fact.value,
      };
    }
  }

  return next;
}

function sourceKindToBaselineSource(
  sourceKind: OrgProfileFact["sourceKind"],
): BaselineRecord["source"] {
  switch (sourceKind) {
    case "confirmed":
      return "confirmed";
    case "user_provided":
    case "imported":
      return "customer_reported";
    case "benchmark_default":
      return "benchmark";
    default:
      return "estimated";
  }
}

function normalizeBaselineRecords(
  profile: EffectiveOrgProfileInput,
  facts: OrgProfileFact[],
): BaselineRecord[] {
  const records = Array.isArray(profile.baselineRecords)
    ? (profile.baselineRecords as BaselineRecord[])
    : [];
  const encounterOverrides = getEncounterOverrides(profile.tier1Inputs);
  const inpatient = finiteNonNegativeEncounterVolume(
    encounterOverrides.inpatient,
  );
  let nextRecords = records;

  if (inpatient != null) {
    let found = false;
    nextRecords = records.map((record) => {
      if (record.key !== "inpatient_discharges") return record;
      found = true;
      if (
        record.source === "confirmed" ||
        record.source === "customer_reported"
      ) {
        return record;
      }
      return {
        ...record,
        value: Math.round(inpatient),
        source: "customer_reported" as const,
        sourceLabel: "Org profile inpatient encounter volume",
      };
    });

    if (!found) {
      nextRecords = [
        ...nextRecords,
        {
          key: "inpatient_discharges",
          value: Math.round(inpatient),
          source: "customer_reported",
          sourceLabel: "Org profile inpatient encounter volume",
        },
      ];
    }
  }

  const byKeyScope = new Map<string, BaselineRecord>();
  for (const record of nextRecords) {
    byKeyScope.set(
      `${record.key}::${serializeBaselineScope(record.scope)}`,
      record,
    );
  }
  for (const fact of facts) {
    if (!fact.fieldPath.startsWith("baseline_records.")) continue;
    const key = fact.fieldPath.slice("baseline_records.".length);
    if (typeof fact.value !== "number" || !Number.isFinite(fact.value)) {
      continue;
    }
    const scope = fact.scope as BaselineRecord["scope"] | undefined;
    const mapKey = `${key}::${serializeBaselineScope(scope)}`;
    const prior = byKeyScope.get(mapKey);
    if (
      !prior ||
      orgProfileFactSourceTrust(fact.sourceKind) >
        orgProfileFactSourceTrust(
          prior.source === "confirmed"
            ? "confirmed"
            : prior.source === "customer_reported"
              ? "user_provided"
              : prior.source === "benchmark"
                ? "benchmark_default"
                : "estimated",
        )
    ) {
      byKeyScope.set(mapKey, {
        key,
        value: fact.value,
        source: sourceKindToBaselineSource(fact.sourceKind),
        sourceLabel: fact.sourceLabel,
        scope,
        providedBy: fact.providedBy,
        updatedBy: fact.updatedBy,
      });
    }
  }

  return [...byKeyScope.values()];
}

/**
 * Fold `organizationalProfileFacts` (the attribution layer) into the stored
 * `profileData` + `baselineRecords` columns, producing the EFFECTIVE profile.
 * This is the representation generation computes against — and, via
 * `resolveModelContext`, the one the server recompute and the frontend live ROI
 * must use too, so all four agree on the org-derived formula context.
 */
export function buildEffectiveOrgProfileData(
  profile: EffectiveOrgProfileInput,
): {
  profileData: ProfileData;
  baselineRecords: BaselineRecord[];
  facts: OrgProfileFact[];
} {
  const facts = getFacts(profile);
  const profileData = applyFactValuesToProfileData(
    normalizeProfileData(profile),
    facts,
  );
  return {
    profileData,
    baselineRecords: normalizeBaselineRecords(profile, facts),
    facts,
  };
}
